import React, { useState, useEffect } from 'react';

import { Modal } from 'components/containers';
import OptionDialog from 'components/containers/Dialog/OptionDialog';
import {
  DatePicker,
  Form,
  MonthYearPicker,
  RadioGroup,
  TextField,
  useForm,
} from 'components/form';
import { Button, IconButton } from 'components/elements';
import { X } from 'components/icons';
import { useTranslationX } from 'i18n';
import { messageService } from 'services';
import { UI_BUTTON_SPACING } from 'consts';
import { useRequest } from 'apis';
import { ProjectStatus } from 'pages/project/types';

import { ProjectDetailsType } from '../ProjectDashboard';

import styles from './EditProjectModal.module.scss';

type Props = {
  visible: boolean;
  onClose: () => void;
  data: ProjectDetailsType;
  projectId: number;
  projectStatus: ProjectStatus;
  onSuccess?: () => void;
};

type EditSection = 'bankData' | 'projectData' | 'executionStart' | 'projectStatus' | null;

const EditProjectModal: React.FC<Props> = ({
  visible,
  onClose,
  data,
  projectId,
  projectStatus,
  onSuccess,
}) => {
  const { tx: dashboardTx } = useTranslationX('dashboard.projectDetails', 'project');
  const { tx: editTx } = useTranslationX('dashboard.edit', 'project');

  const [editingSection, setEditingSection] = useState<EditSection>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submit'>('idle');

  const request = useRequest();

  // API Functions
  const updateProject = (updateData: any) => {
    const source = request<{ success: boolean; message?: string }>({
      url: `/projetos/${projectId}/atualizar`,
      method: 'PUT',
      data: updateData,
      onSuccess: result => {
        if (result.success) {
          messageService.success('Projeto atualizado com sucesso');
          setEditingSection(null);
          onSuccess?.();
        } else {
          messageService.error(result.message || 'Erro ao atualizar projeto');
        }
      },
      onError: error => {
        console.error('Erro na requisição:', error);
        messageService.error('Erro ao atualizar projeto');
      },
    });

    return () => source.cancel();
  };

  // Form Submit Handlers
  const handleBankDataSubmit = (formData: { [key: string]: any }) => {
    const fontes = formData.fontes as {
      gpf: string;
      banco: string;
      agencia: string;
      conta: string;
    }[];
    const fontesRecurso = data.fontes?.map((fonte, index) => ({
      gpf: fonte.gpf,
      conta: {
        banco: fontes[index]?.banco,
        agencia: fontes[index]?.agencia,
        numero: fontes[index]?.conta,
      },
    }));

    updateProject({ fontesRecurso });
  };

  const handleProjectDataSubmit = (formData: { [key: string]: any }) => {
    const updateData: any = {};

    updateProject({
      ...updateData,
      identificacao: {
        srinfo: formData.srinfo,
        dataAprovacaoConselho: formData.dataAprovacaoConselho,
      },
    });
  };

  const handleExecutionStartSubmit = (formData: { [key: string]: any }) => {
    const updateData: any = {};

    if (formData.dataInicioExecucao) {
      updateData.dataInicioExecucao = formData.dataInicioExecucao;
    }

    if (formData.mesInicioExecucao) {
      updateData.mesInicioExecucao = formData.mesInicioExecucao;
    }

    updateProject(updateData);
  };

  const handleProjectStatusSubmit = (formData: { [key: string]: any }) => {
    updateProject({ estado: formData.status });
  };

  const handleFormSubmit = (formData: { [key: string]: any }) => {
    switch (editingSection) {
      case 'bankData':
        handleBankDataSubmit(formData);
        break;
      case 'projectData':
        handleProjectDataSubmit(formData);
        break;
      case 'executionStart':
        handleExecutionStartSubmit(formData);
        break;
      case 'projectStatus':
        handleProjectStatusSubmit(formData);
        break;
    }
  };

  // UI Event Handlers
  const handleSectionEdit = (section: EditSection) => {
    setEditingSection(section);
  };

  const handleCancelEdit = () => {
    setShowConfirmation(true);
  };

  const handleClose = () => {
    if (editingSection) {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  };

  const confirmCancel = () => {
    setShowConfirmation(false);
    setEditingSection(null);
  };

  // Render Functions
  const renderSectionSelection = () => (
    <div className={styles.sectionSelection}>
      <h2>{editTx('title')}</h2>
      <p>{editTx('selectSection')}</p>

      <div className={styles.sectionButtons}>
        <Button theme="light" onClick={() => handleSectionEdit('bankData')}>
          {dashboardTx('bankData')}
        </Button>

        <Button theme="light" onClick={() => handleSectionEdit('projectData')}>
          {dashboardTx('projectIdentification')}
        </Button>

        {/* <Button theme="light" onClick={() => handleSectionEdit('executionStart')}>
          {dashboardTx('executionStart')}
        </Button> */}
      </div>
    </div>
  );

  const renderBankDataForm = () => (
    <Form onSubmit={({ requestBody }) => handleFormSubmit(requestBody)}>
      <BankDataFormContent
        data={data}
        submitStatus={submitStatus}
        setSubmitStatus={setSubmitStatus}
        onCancel={handleCancelEdit}
      />
    </Form>
  );

  const renderProjectDataForm = () => (
    <Form onSubmit={({ requestBody }) => handleFormSubmit(requestBody)}>
      <ProjectDataFormContent
        data={data}
        submitStatus={submitStatus}
        setSubmitStatus={setSubmitStatus}
        onCancel={handleCancelEdit}
      />
    </Form>
  );

  const renderExecutionStartForm = () => (
    <Form onSubmit={({ requestBody }) => handleFormSubmit(requestBody)}>
      <ExecutionStartFormContent
        data={data}
        submitStatus={submitStatus}
        setSubmitStatus={setSubmitStatus}
        onCancel={handleCancelEdit}
      />
    </Form>
  );

  const renderProjectStatusForm = () => (
    <Form onSubmit={({ requestBody }) => handleFormSubmit(requestBody)}>
      <ProjectStatusFormContent
        projectStatus={projectStatus}
        submitStatus={submitStatus}
        setSubmitStatus={setSubmitStatus}
        onCancel={handleCancelEdit}
      />
    </Form>
  );

  return (
    <>
      <Modal visible={visible} onClose={handleClose} onBlur={handleClose} width={700}>
        <div className={styles.editModal}>
          <div className={styles.closeIcon}>
            <IconButton icon={X} onClick={handleClose} />
          </div>

          {!editingSection && renderSectionSelection()}
          {editingSection === 'bankData' && renderBankDataForm()}
          {editingSection === 'projectData' && renderProjectDataForm()}
          {editingSection === 'executionStart' && renderExecutionStartForm()}
          {editingSection === 'projectStatus' && renderProjectStatusForm()}
        </div>
      </Modal>

      <OptionDialog
        visible={showConfirmation}
        title={editTx('confirmTitle')}
        message={editTx('confirmMessage')}
        options={[
          { text: editTx('cancel'), value: 1 },
          { text: editTx('confirm'), value: 2 },
        ]}
        onSelect={value => {
          if (value === 2) {
            confirmCancel();
          }
          setShowConfirmation(false);
        }}
      />
    </>
  );
};

const BankDataFormContent: React.FC<{
  data: ProjectDetailsType;
  submitStatus: 'idle' | 'submit';
  setSubmitStatus: (status: 'idle' | 'submit') => void;
  onCancel: () => void;
}> = ({ data, submitStatus, setSubmitStatus, onCancel }) => {
  const { tx } = useTranslationX('create', 'project');
  const { tx: dashboardTx } = useTranslationX('dashboard.projectDetails', 'project');
  const { tx: editTx } = useTranslationX('dashboard.edit', 'project');
  const form = useForm();

  useEffect(() => {
    if (submitStatus === 'submit') {
      form.submit();
      setSubmitStatus('idle');
    }
  }, [form, submitStatus, setSubmitStatus]);

  return (
    <div className={styles.formContainer}>
      <h2>{dashboardTx('bankData')}</h2>

      {data.fontes?.map((fonte, index) => (
        <div key={fonte.id} className={styles.fontesGroup}>
          <h4>{fonte.nome}</h4>

          <div className={styles.fieldsRow}>
            <TextField
              name={`fontes[${index}].gpf`}
              label={tx('gpf')}
              value={fonte.gpf || ''}
              type="readonly"
              metadata={{
                rules: {
                  required: true,
                },
              }}
            />

            <TextField
              name={`fontes[${index}].banco`}
              label={tx('bank')}
              value={fonte.conta?.banco || ''}
              metadata={{ rules: { required: true } }}
            />

            <TextField
              name={`fontes[${index}].agencia`}
              label={tx('agency')}
              value={fonte.conta?.agencia || ''}
              metadata={{
                rules: {
                  required: true,
                  maxlength: 6,
                  mask: { pattern: 'agency' },
                },
              }}
            />

            <TextField
              name={`fontes[${index}].conta`}
              label={tx('account')}
              value={fonte.conta?.numero || ''}
              metadata={{
                rules: {
                  required: true,
                  mask: { pattern: 'account' },
                },
              }}
            />
          </div>
        </div>
      ))}

      <div className={styles.formActions}>
        <Button type="secondary" onClick={onCancel}>
          {tx('cancel')}
        </Button>
        <Button
          type="primary"
          onClick={() => setSubmitStatus('submit')}
          style={{ root: { marginLeft: UI_BUTTON_SPACING } }}
        >
          {editTx('save')}
        </Button>
      </div>
    </div>
  );
};

const ProjectDataFormContent: React.FC<{
  data: ProjectDetailsType;
  submitStatus: 'idle' | 'submit';
  setSubmitStatus: (status: 'idle' | 'submit') => void;
  onCancel: () => void;
}> = ({ data, submitStatus, setSubmitStatus, onCancel }) => {
  const { tx } = useTranslationX('create', 'project');
  const { tx: dashboardTx } = useTranslationX('dashboard.projectDetails', 'project');
  const { tx: editTx } = useTranslationX('dashboard.edit', 'project');
  const form = useForm();

  useEffect(() => {
    if (submitStatus === 'submit') {
      form.submit();
      setSubmitStatus('idle');
    }
  }, [form, submitStatus, setSubmitStatus]);

  return (
    <div className={styles.formContainer}>
      <h2>{dashboardTx('projectIdentification')}</h2>

      <TextField
        name="srinfo"
        label={tx('identification')}
        value={data.numero}
        metadata={{
          rules: {
            maxlength: 10,
            case: 'upper',
          },
        }}
      />

      <DatePicker
        name="dataAprovacaoConselho"
        label={tx('boardApprovalDate')}
        metadata={{ rules: { required: true } }}
        value={data.dataAprovacaoConselho}
      />

      <div className={styles.formActions}>
        <Button type="secondary" onClick={onCancel}>
          {tx('cancel')}
        </Button>
        <Button
          type="primary"
          onClick={() => setSubmitStatus('submit')}
          style={{ root: { marginLeft: UI_BUTTON_SPACING } }}
        >
          {editTx('save')}
        </Button>
      </div>
    </div>
  );
};

const ExecutionStartFormContent: React.FC<{
  data: ProjectDetailsType;
  submitStatus: 'idle' | 'submit';
  setSubmitStatus: (status: 'idle' | 'submit') => void;
  onCancel: () => void;
}> = ({ data, submitStatus, setSubmitStatus, onCancel }) => {
  const { tx } = useTranslationX('create', 'project');
  const { tx: dashboardTx } = useTranslationX('dashboard.projectDetails', 'project');
  const { tx: editTx } = useTranslationX('dashboard.edit', 'project');
  const form = useForm();

  useEffect(() => {
    if (submitStatus === 'submit') {
      form.submit();
      setSubmitStatus('idle');
    }
  }, [form, submitStatus, setSubmitStatus]);

  return (
    <div className={styles.formContainer}>
      <h2>{dashboardTx('executionStart')}</h2>

      <DatePicker
        name="dataInicioExecucao"
        label={tx('startDate')}
        metadata={{ rules: { required: true } }}
      />

      <MonthYearPicker
        name="mesInicioExecucao"
        label={tx('startMonth')}
        metadata={{ rules: { required: true } }}
      />

      <div className={styles.formActions}>
        <Button type="secondary" onClick={onCancel}>
          {tx('cancel')}
        </Button>
        <Button
          type="primary"
          onClick={() => setSubmitStatus('submit')}
          style={{ root: { marginLeft: UI_BUTTON_SPACING } }}
        >
          {editTx('save')}
        </Button>
      </div>
    </div>
  );
};

const ProjectStatusFormContent: React.FC<{
  projectStatus: ProjectStatus;
  submitStatus: 'idle' | 'submit';
  setSubmitStatus: (status: 'idle' | 'submit') => void;
  onCancel: () => void;
}> = ({ projectStatus, submitStatus, setSubmitStatus, onCancel }) => {
  const { tx: editTx } = useTranslationX('dashboard.edit', 'project');
  const form = useForm();

  useEffect(() => {
    if (submitStatus === 'submit') {
      form.submit();
      setSubmitStatus('idle');
    }
  }, [form, submitStatus, setSubmitStatus]);

  const statusReverseMap: { [key in ProjectStatus]?: string } = {
    EXE: 'EXECUTING',
    FIN: 'FINALIZED',
    CAN: 'CANCELED',
  };

  return (
    <div className={styles.formContainer}>
      <h2>{editTx('projectStatus')}</h2>

      <RadioGroup
        name="status"
        label={editTx('selectStatus')}
        value={statusReverseMap[projectStatus] || 'EXECUTING'}
        columns={1}
        metadata={{
          rules: { required: true },
          options: [
            { value: 'EXECUTING', text: editTx('executing') },
            { value: 'FINALIZED', text: editTx('finalized') },
            { value: 'CANCELED', text: editTx('canceled') },
          ],
        }}
      />

      <div className={styles.formActions}>
        <Button type="secondary" onClick={onCancel}>
          {editTx('cancel')}
        </Button>
        <Button
          type="primary"
          onClick={() => setSubmitStatus('submit')}
          style={{ root: { marginLeft: UI_BUTTON_SPACING } }}
        >
          {editTx('save')}
        </Button>
      </div>
    </div>
  );
};

export default EditProjectModal;
